import type { AxiosResponse } from 'axios';

import { type ILicenseTypeConfigResponse, type ILicenseTypeResponse } from '../../modules/License/types/response';

import { request } from '../requestService';

import { Operators, type SearchResult } from '../../@types/serviceTypes';
import { type IRestriction } from '../../types';

import { LPCO_ADMIN_SERVER } from '../../config';

const ENDPOINTS = {
  licenseType: 'licenseType',
  search: 'search',
  getById: 'getById',
  ogaStatusList: 'ogaStatusList',
};

export type TLicenseType = Pick<ILicenseTypeResponse, 'licenseTypeCode' | 'licenseTypeName' | 'id'>;

export type TOgaStatus = {
  status: string;
  statusInNatLng: string;
};

export type TLicenseTypesResponse = SearchResult<TLicenseType>;

export function getLicenseTypeByCode(licenseTypeCode: string): Promise<AxiosResponse<ILicenseTypeConfigResponse>> {
  return request({
    url: `${ENDPOINTS.licenseType}/${licenseTypeCode}`,
  });
}

export function getLicenseTypeById(id: number): Promise<AxiosResponse<ILicenseTypeConfigResponse>> {
  return request({
    url: `${ENDPOINTS.licenseType}/${ENDPOINTS.getById}/${id}`,
  });
}

export function getLicenseTypesOgaStatuses(): Promise<AxiosResponse<TOgaStatus[]>> {
  return request({
    url: `${ENDPOINTS.licenseType}/${ENDPOINTS.ogaStatusList}`,
  });
}

export function getLicenseTypes({
  searchValue = '',
  licenseTypeNature,
  isCreatePage = false,
}: {
  searchValue?: string;
  licenseTypeNature?: string;
  isCreatePage?: boolean;
}): Promise<AxiosResponse<TLicenseTypesResponse>> {
  const restrictions: IRestriction[] = [
    {
      field: 'licenseTypeCode',
      value: searchValue,
      operator: Operators.STARTS_WITH,
    },
    {
      field: 'eov',
      operator: Operators.IS_NULL,
    },
  ];

  if (licenseTypeNature) {
    restrictions.push({
      field: 'licenseTypeNature',
      value: licenseTypeNature,
      operator: Operators.EQUALS,
    });
  }

  if (isCreatePage) {
    restrictions.push({
      field: 'featureFlags.submissionThroughUiEnabled',
      value: true,
      operator: Operators.EQUALS,
    });
  }

  return request({
    method: 'POST',
    baseURL: LPCO_ADMIN_SERVER,
    url: `${ENDPOINTS.licenseType}/${ENDPOINTS.search}`,
    data: {
      offset: 0,
      max: 10000,
      selectFields: ['licenseTypeCode', 'licenseTypeName', 'id'],
      distinct: true,
      restrictions,
    },
  });
}

export function getLicenseTypesByCodes(licenseTypeCodes: string[]): Promise<AxiosResponse<TLicenseTypesResponse>> {
  return request({
    method: 'POST',
    baseURL: LPCO_ADMIN_SERVER,
    url: `${ENDPOINTS.licenseType}/${ENDPOINTS.search}`,
    data: {
      offset: 0,
      max: 10000,
      restrictBy: 'AND',
      selectFields: ['licenseTypeCode', 'licenseTypeName', 'id'],
      distinct: true,
      restrictions: [
        {
          values: licenseTypeCodes,
          field: 'licenseTypeCode',
          operator: Operators.IN,
        },
        {
          field: 'eov',
          operator: Operators.IS_NULL,
        },
      ],
    },
  });
}
